// =============================================================================
// Zakładka Przypisania — jedna centralna kartoteka `print_printer_assignments`
// (purpose × module → drukarka) w miejsce trzech rozjechanych systemów
// konfiguracji PackFlow (localStorage / tabele / JSON w settings).
//
// Blokada niezgodności następuje TUTAJ, w miejscu konfiguracji: Select drukarki
// oferuje wyłącznie drukarki mówiące protokołem zgodnym z przeznaczeniem
// (`PROTOKOLY_PRZEZNACZEN` w status.ts) — klasa błędu „PDF wysłany na EPL
// drukuje śmieci" umiera zanim powstanie zadanie.
//
// Wiersz „(wszystkie)" (module=NULL) to jawne dziedziczenie: moduł bez
// własnego wiersza dostaje drukarkę z tego wiersza — print-core rozwiązuje od
// najściślejszego dopasowania (plan §6.1), a ta tabela mówi to samo słowami.
// =============================================================================

import { PRINT_PURPOSES, type PrintPurpose } from '@insolitum/types';
import {
  Alert,
  Badge,
  Button,
  Modal,
  ModalFooter,
  Select,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from '@insolitum/ui';
import { Plus, RefreshCw, Route, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MODULE_REGISTRY } from '../../../modules/registry';
import { useAuth } from '../../../providers/AuthProvider';

import {
  KOMUNIKAT_BRAK_PRINT_CORE,
  PrintCoreError,
  printCoreDostepny,
  usunPrzypisanie,
  zapiszPrzypisanie,
} from './printCoreApi';
import { PROTOKOLY_PRZEZNACZEN, drukarkiZgodne } from './status';
import { useAssignments, usePrinters, useSites, type PrzypisanieWiersz } from './usePrintingData';

/** Etykiety przeznaczeń w panelu — słownik z `PRINT_PURPOSES` (typy drutu). */
const ETYKIETY_PRZEZNACZEN: Record<PrintPurpose, string> = {
  packing_list: 'Lista pakowania',
  product_label: 'Etykieta produktu',
  shipping_label: 'Etykieta wysyłkowa',
  package_label: 'Etykieta paczki',
  invoice: 'Faktura',
  wz: 'WZ',
  test_page: 'Strona testowa',
};

const WSZYSTKIE_MODULY = '(wszystkie)';

interface Props {
  /** Filtr ?site= — przypisania MAJĄ kolumnę site_id (migracja 186). */
  siteId: string | null;
}

export function AssignmentsTab({ siteId }: Props) {
  const { t } = useTranslation();
  const { currentOrganization, hasRole } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const admin = hasRole(['owner', 'admin']);
  const zapisMozliwy = printCoreDostepny();

  const { przypisania, loading, blad, odswiez } = useAssignments();
  const { drukarki } = usePrinters();
  const { lokalizacje } = useSites();

  const [modalOtwarty, setModalOtwarty] = useState(false);
  const [bladAkcji, setBladAkcji] = useState<string | null>(null);
  const [zapisywany, setZapisywany] = useState<string | null>(null);

  const nazwaLokalizacji = useMemo(() => {
    const mapa = new Map(lokalizacje.map((s) => [s.id, s.name]));
    return (id: string | null) => (id ? (mapa.get(id) ?? id.slice(0, 8)) : null);
  }, [lokalizacje]);

  // Filtr lokalizacji strony: wiersz bez site_id obowiązuje wszędzie, więc
  // pokazujemy go zawsze; wiersz z site_id tylko przy zgodnym filtrze.
  const widoczne = useMemo(
    () => przypisania.filter((p) => !siteId || !p.site_id || p.site_id === siteId),
    [przypisania, siteId]
  );

  const zmienDrukarke = async (wiersz: PrzypisanieWiersz, printerId: string) => {
    if (!orgId || !wiersz.purpose) return;
    setZapisywany(wiersz.id);
    setBladAkcji(null);
    try {
      await zapiszPrzypisanie(orgId, {
        purpose: wiersz.purpose,
        module: wiersz.module,
        site_id: wiersz.site_id,
        printer_id: printerId,
      });
      await odswiez();
    } catch (err) {
      setBladAkcji(err instanceof PrintCoreError ? err.message : 'Zapis nie powiódł się');
    } finally {
      setZapisywany(null);
    }
  };

  const usun = async (wiersz: PrzypisanieWiersz) => {
    if (!orgId || !wiersz.purpose) return;
    if (!confirm('Usunąć to przypisanie? Moduł wróci do dziedziczenia albo ręcznego wyboru.')) {
      return;
    }
    setBladAkcji(null);
    try {
      await usunPrzypisanie(orgId, {
        purpose: wiersz.purpose,
        module: wiersz.module,
        site_id: wiersz.site_id,
      });
      await odswiez();
    } catch (err) {
      setBladAkcji(err instanceof PrintCoreError ? err.message : 'Usunięcie nie powiodło się');
    }
  };

  const kolumn = 5;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-aurora-muted">
          {t(
            'printing.assignmentsHint',
            'Moduł zleca przeznaczenie („co drukuję"), a print-core wybiera drukarkę z tej tabeli — od najściślejszego dopasowania do wiersza „(wszystkie)".'
          )}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => void odswiez()} className="iconbtn" title="Odśwież">
            <RefreshCw className="w-5 h-5" />
          </button>
          {admin && (
            <Button
              onClick={() => setModalOtwarty(true)}
              disabled={!zapisMozliwy}
              title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
            >
              <Plus className="w-4 h-4" />
              {t('printing.addAssignment', 'Dodaj przypisanie')}
            </Button>
          )}
        </div>
      </div>

      {blad && (
        <Alert variant="error">
          {blad}{' '}
          <button onClick={() => void odswiez()} className="underline">
            {t('printing.retry', 'Spróbuj ponownie')}
          </button>
        </Alert>
      )}
      {bladAkcji && (
        <Alert variant="error" dismissible onDismiss={() => setBladAkcji(null)}>
          {bladAkcji}
        </Alert>
      )}

      <div className="card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('printing.colPurpose', 'Przeznaczenie')}</TableHead>
              <TableHead>{t('printing.colModule', 'Moduł')}</TableHead>
              <TableHead>{t('printing.colSite', 'Lokalizacja')}</TableHead>
              <TableHead>{t('printing.colPrinter', 'Drukarka')}</TableHead>
              <TableHead className="w-12" aria-label="Akcje" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableLoading colSpan={kolumn} rows={4} />
            ) : widoczne.length === 0 ? (
              <TableEmpty
                colSpan={kolumn}
                icon={<Route className="w-10 h-10" />}
                message={t('printing.noAssignments', 'Brak przypisań')}
                description={t(
                  'printing.noAssignmentsHint',
                  'Bez przypisań zlecenie po przeznaczeniu zakończy się błędem „no_printer_for_purpose" — moduły będą wymagać jawnego wyboru drukarki.'
                )}
              />
            ) : (
              widoczne.map((wiersz) => {
                const przeznaczenie = wiersz.purpose as PrintPurpose | null;
                const zgodne = przeznaczenie
                  ? drukarkiZgodne(przeznaczenie, drukarki)
                  : drukarki.filter((d) => d.is_active);
                return (
                  <TableRow key={wiersz.id}>
                    <TableCell className="font-medium text-aurora-text">
                      {przeznaczenie
                        ? (ETYKIETY_PRZEZNACZEN[przeznaczenie] ?? wiersz.purpose)
                        : '—'}
                      {przeznaczenie && (
                        <span className="ml-2 mono text-xs text-aurora-subtle">
                          {wiersz.purpose}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {wiersz.module ?? (
                        <Badge variant="default" size="sm">
                          {WSZYSTKIE_MODULY}
                        </Badge>
                      )}
                      {!wiersz.module && (
                        <span className="ml-2 text-xs text-aurora-subtle">
                          {t('printing.inheritedBy', 'dziedziczą moduły bez własnego wiersza')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{nazwaLokalizacji(wiersz.site_id) ?? WSZYSTKIE_MODULY}</TableCell>
                    <TableCell>
                      <div className="w-56">
                        <Select
                          aria-label={`Drukarka dla ${wiersz.purpose ?? ''}`}
                          size="sm"
                          value={wiersz.printer_id}
                          disabled={!admin || !zapisMozliwy || zapisywany === wiersz.id}
                          title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
                          onChange={(e) => void zmienDrukarke(wiersz, e.target.value)}
                          options={(() => {
                            const opcje = zgodne.map((d) => ({
                              value: d.id,
                              label: `${d.name} (${d.protocol ?? 'PDF'})`,
                            }));
                            // Obecna drukarka mogła stać się niezgodna (zmiana
                            // protokołu) — pokazujemy ją z ostrzeżeniem zamiast
                            // cicho podmieniać wybór admina.
                            if (!opcje.some((o) => o.value === wiersz.printer_id)) {
                              const obecna = drukarki.find((d) => d.id === wiersz.printer_id);
                              opcje.unshift({
                                value: wiersz.printer_id,
                                label: `${obecna?.name ?? wiersz.printer_id.slice(0, 8)} — NIEZGODNA`,
                              });
                            }
                            return opcje;
                          })()}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {admin && (
                        <button
                          onClick={() => void usun(wiersz)}
                          disabled={!zapisMozliwy}
                          title={zapisMozliwy ? 'Usuń przypisanie' : KOMUNIKAT_BRAK_PRINT_CORE}
                          className="iconbtn w-8 h-8 hover:text-red-400 disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-aurora-subtle">
        {t(
          'printing.autoprintNote',
          'Automatyczny wydruk i liczbę kopii konfigurujesz w ustawieniach danego modułu — ta tabela mówi tylko, KTÓRA drukarka obsługuje dane przeznaczenie.'
        )}
      </p>

      {modalOtwarty && orgId && (
        <ModalNowegoPrzypisania
          orgId={orgId}
          siteId={siteId}
          drukarki={drukarki}
          istniejace={przypisania}
          onZamknij={() => setModalOtwarty(false)}
          onZapisano={() => {
            setModalOtwarty(false);
            void odswiez();
          }}
        />
      )}
    </div>
  );
}

// ── Modal nowego przypisania ─────────────────────────────────────────────────

function ModalNowegoPrzypisania({
  orgId,
  siteId,
  drukarki,
  istniejace,
  onZamknij,
  onZapisano,
}: {
  orgId: string;
  siteId: string | null;
  drukarki: ReturnType<typeof usePrinters>['drukarki'];
  istniejace: PrzypisanieWiersz[];
  onZamknij: () => void;
  onZapisano: () => void;
}) {
  const { t } = useTranslation();
  const [przeznaczenie, setPrzeznaczenie] = useState<PrintPurpose>('product_label');
  const [modul, setModul] = useState('');
  const [drukarkaId, setDrukarkaId] = useState('');
  const [zapisuje, setZapisuje] = useState(false);
  const [bladZapisu, setBladZapisu] = useState<string | null>(null);

  const zgodne = drukarkiZgodne(przeznaczenie, drukarki);
  const dozwoloneProtokoly = PROTOKOLY_PRZEZNACZEN[przeznaczenie]?.join(', ') ?? '';

  // Wiersz „(wszystkie)" dla tego przeznaczenia — pokazujemy dziedziczenie,
  // zanim admin doda wiersz modułowy, żeby wiedział, co nadpisuje.
  const dziedziczoneZ = istniejace.find(
    (p) =>
      p.purpose === przeznaczenie &&
      p.module === null &&
      (!siteId || !p.site_id || p.site_id === siteId)
  );
  const nazwaDziedziczonej = dziedziczoneZ
    ? (drukarki.find((d) => d.id === dziedziczoneZ.printer_id)?.name ?? '?')
    : null;

  const zapisz = async () => {
    if (!drukarkaId) {
      setBladZapisu('Wybierz drukarkę');
      return;
    }
    setZapisuje(true);
    setBladZapisu(null);
    try {
      await zapiszPrzypisanie(orgId, {
        purpose: przeznaczenie,
        module: modul || null,
        site_id: siteId,
        printer_id: drukarkaId,
      });
      onZapisano();
    } catch (err) {
      setBladZapisu(err instanceof PrintCoreError ? err.message : 'Zapis nie powiódł się');
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <Modal
      open
      onClose={onZamknij}
      title={t('printing.newAssignment', 'Nowe przypisanie')}
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onZamknij}>
            {t('printing.cancel', 'Anuluj')}
          </Button>
          <Button onClick={() => void zapisz()} loading={zapisuje}>
            {t('printing.save', 'Zapisz')}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {bladZapisu && <Alert variant="error">{bladZapisu}</Alert>}
        <Select
          label={t('printing.colPurpose', 'Przeznaczenie')}
          value={przeznaczenie}
          onChange={(e) => {
            setPrzeznaczenie(e.target.value as PrintPurpose);
            setDrukarkaId('');
          }}
          options={PRINT_PURPOSES.map((p) => ({ value: p, label: ETYKIETY_PRZEZNACZEN[p] }))}
        />
        <Select
          label={t('printing.colModule', 'Moduł')}
          value={modul}
          onChange={(e) => setModul(e.target.value)}
          options={[
            { value: '', label: WSZYSTKIE_MODULY },
            ...MODULE_REGISTRY.map((m) => ({ value: m.slug, label: m.navName })),
          ]}
          helperText={
            modul && nazwaDziedziczonej
              ? t('printing.overridesInherited', 'Nadpisze dziedziczone: {{printer}}', {
                  printer: nazwaDziedziczonej,
                })
              : undefined
          }
        />
        {zgodne.length === 0 ? (
          <Alert variant="warning">
            {t(
              'printing.noCompatibleForPurpose',
              'Brak aktywnej drukarki zgodnej z tym przeznaczeniem (wymagane protokoły: {{protocols}}). Dodaj drukarkę w zakładce Drukarki.',
              { protocols: dozwoloneProtokoly }
            )}
          </Alert>
        ) : (
          <Select
            label={t('printing.colPrinter', 'Drukarka')}
            value={drukarkaId}
            onChange={(e) => setDrukarkaId(e.target.value)}
            placeholder={t('printing.pickPrinter', 'Wybierz drukarkę')}
            options={zgodne.map((d) => ({
              value: d.id,
              label: `${d.name} (${d.protocol ?? 'PDF'})`,
            }))}
            helperText={t(
              'printing.compatibleExplain',
              'Lista zawężona do protokołów zgodnych z przeznaczeniem ({{protocols}}) — niezgodna para drukowałaby nieczytelne znaki.',
              { protocols: dozwoloneProtokoly }
            )}
          />
        )}
      </div>
    </Modal>
  );
}
